import type { JSX } from 'react'
import { useState, useEffect } from 'react'
import { Calendar } from 'primereact/calendar'
import { Dropdown } from 'primereact/dropdown'

import { FilterSidebar as FilterSidebarShell } from '../../data/FilterSidebar.js'
import type { AuditFilters } from '../../../hooks/useAuditLog.js'
import { useAuditLogActors } from '../../../hooks/useAuditLogActors.js'
import { useAuditLogResourceTypes } from '../../../hooks/useAuditLogResourceTypes.js'
import { useAuditLogActions } from '../../../hooks/useAuditLogActions.js'

const ACTION_TYPE_OPTIONS = [
  { label: 'Create', value: 'create' },
  { label: 'Update', value: 'update' },
  { label: 'Delete', value: 'delete' },
]

export type AuditLogFilterSource = 'tenant' | 'platform'

export interface AuditLogFilterSidebarProps {
  value: AuditFilters
  onChange: (next: AuditFilters) => void
  /**
   * Callback when Cancel button is clicked in panel variant.
   * Useful for closing the filter panel.
   */
  onCancel?: () => void
  /**
   * Callback when Apply Filters button is clicked in panel variant.
   * Useful for closing the filter panel after applying filters.
   */
  onApply?: () => void
  /**
   * Which audit log surface the filters apply to. Defaults to `'tenant'`
   * for backwards compatibility with the UI-7a tenant audit page.
   *
   * UI-8d Task 4: the platform audit log reuses this sidebar; the prop
   * is forwarded to the placeholder so platform actors (e.g.
   * `system:cron`) get a hint that free-text strings are accepted.
   * The filter shape (`AuditFilters`) is identical for both surfaces.
   */
  source?: AuditLogFilterSource
  /**
   * `'sidebar'` (default) — wraps the fields in the docked
   * `FilterSidebar` shell (border, "Filters" heading, Clear link).
   * Used by `PlatformAuditLogPage`, which renders this permanently
   * docked to the left of the table.
   *
   * `'panel'` — renders just the fields + a "Clear all filters" link,
   * no shell/border. Used by the tenant `AuditLogPage`, which hosts
   * this inside a toggleable `SlidePanel` (that component supplies its
   * own header/border/close button, so a second one here would nest).
   */
  variant?: 'sidebar' | 'panel'
}

/**
 * Controlled filter panel for the audit log page. Does NOT own filter
 * state — every control calls `onChange({ ...value, <field>: ... })`,
 * and `value` is the source of truth.
 *
 * Phase 1 keeps Actor / Action / Resource Type as free-text inputs:
 * - Actor: backend already validates UUID format server-side; auto-
 *   complete over the agents list is a Phase 2 nicety.
 * - Action / Resource Type: no distinct-values endpoint exists yet,
 *   so a dropdown would be misleading (it'd hardcode a subset).
 *
 * Date range uses PrimeReact's range Calendar. The calendar emits
 * `[Date, null]` after the first click, full `[Date, Date]` after the
 * second. We mirror the RecordingsFilterSidebar pattern and only patch
 * the filter once BOTH ends are present — a one-ended range means
 * "no date filter applied".
 *
 * The Clear button (from FilterSidebarShell) resets to `{}` so an
 * absent field is the no-filter signal everywhere (matches the
 * `AuditFilters` shape — all keys optional).
 */
export function AuditLogFilterSidebar({
  value,
  onChange,
  onCancel,
  onApply,
  variant = 'sidebar',
}: AuditLogFilterSidebarProps): JSX.Element {
  // Local draft state for panel variant
  const [draftValues, setDraftValues] = useState<AuditFilters>(value)
  const { actors, loading: actorsLoading } = useAuditLogActors()
  const { resourceTypes, loading: resourceTypesLoading } = useAuditLogResourceTypes()
  const { actions, loading: actionsLoading } = useAuditLogActions()

  // Sync draft values when external value changes (e.g., from Clear button or Apply)
  useEffect(() => {
    if (variant === 'panel') {
      setDraftValues(value)
    }
  }, [value, variant])

  /**
   * Copies every defined field off `value` — never assigns `undefined`
   * (the project has `exactOptionalPropertyTypes: true`, which forbids
   * that), so callers can spread this and then delete/set individual keys
   * without missing a field every time `AuditFilters` grows.
   */
  function preservedFields(base: AuditFilters): AuditFilters {
    const cleaned: AuditFilters = {}
    if (base.actorId !== undefined) cleaned.actorId = base.actorId
    if (base.action !== undefined) cleaned.action = base.action
    if (base.actionType !== undefined) cleaned.actionType = base.actionType
    if (base.resourceType !== undefined) cleaned.resourceType = base.resourceType
    if (base.resourceId !== undefined) cleaned.resourceId = base.resourceId
    if (base.from !== undefined) cleaned.from = base.from
    if (base.to !== undefined) cleaned.to = base.to
    return cleaned
  }

  /**
   * Patch helper for draft values. Sets a single field; an empty/undefined value REMOVES
   * the key entirely so the filter stays minimal.
   */
  function setDraftField<K extends keyof AuditFilters>(
    key: K,
    next: AuditFilters[K] | undefined,
  ): void {
    const cleaned = preservedFields(draftValues)
    if (next === undefined || next === '' || next === null) {
      delete cleaned[key]
    } else {
      cleaned[key] = next
    }
    setDraftValues(cleaned)
  }

  const fields = (
    <>
      <div className="space-y-5">
        <label className="flex flex-col gap-2">
          <span className="text-sm font-semibold text-gray-700 flex items-center gap-2">
            <i className="pi pi-user text-gray-400" />
            Actor
          </span>
          <Dropdown
            aria-label="Filter by actor"
            value={variant === 'panel' ? (draftValues.actorId ?? null) : (value.actorId ?? null)}
            options={actors.map((a) => ({ label: a.name, value: a.id }))}
            onChange={(e) => {
              const newValue = e.value as string | undefined
              if (variant === 'panel') {
                setDraftField('actorId', newValue)
              } else {
                setField('actorId', newValue)
              }
            }}
            placeholder="Select actor"
            showClear
            className="w-full"
            loading={actorsLoading}
            pt={{
              root: { className: 'border-gray-200 rounded-sm shadow-sm' },
              input: { className: 'text-sm py-2.5' },
              panel: { className: 'rounded-sm shadow-lg border border-gray-200' },
            }}
          />
        </label>

        <label className="flex flex-col gap-2">
          <span className="text-sm font-semibold text-gray-700 flex items-center gap-2">
            <i className="pi pi-bolt text-gray-400" />
            Action
          </span>
          <Dropdown
            aria-label="Filter by action"
            value={variant === 'panel' ? (draftValues.action ?? null) : (value.action ?? null)}
            options={actions.map((a) => ({ label: a, value: a }))}
            onChange={(e) => {
              const newValue = e.value as string | undefined
              if (variant === 'panel') {
                setDraftField('action', newValue)
              } else {
                setField('action', newValue)
              }
            }}
            placeholder="Select action"
            showClear
            className="w-full"
            loading={actionsLoading}
            pt={{
              root: { className: 'border-gray-200 rounded-sm shadow-sm' },
              input: { className: 'text-sm py-2.5' },
              panel: { className: 'rounded-sm shadow-lg border border-gray-200' },
            }}
          />
        </label>

        <label className="flex flex-col gap-2">
          <span className="text-sm font-semibold text-gray-700 flex items-center gap-2">
            <i className="pi pi-tag text-gray-400" />
            Action type
          </span>
          <Dropdown
            aria-label="Filter by action type"
            value={variant === 'panel' ? (draftValues.actionType ?? null) : (value.actionType ?? null)}
            options={ACTION_TYPE_OPTIONS}
            onChange={(e) => {
              const newValue = (e.value as AuditFilters['actionType']) ?? undefined
              if (variant === 'panel') {
                setDraftField('actionType', newValue)
              } else {
                setField('actionType', newValue)
              }
            }}
            placeholder="Select type"
            showClear
            className="w-full"
            pt={{
              root: { className: 'border-gray-200 rounded-sm shadow-sm' },
              input: { className: 'text-sm py-2.5' },
              panel: { className: 'rounded-sm shadow-lg border border-gray-200' },
            }}
          />
        </label>

        <label className="flex flex-col gap-2">
          <span className="text-sm font-semibold text-gray-700 flex items-center gap-2">
            <i className="pi pi-file text-gray-400" />
            Resource type
          </span>
          <Dropdown
            aria-label="Filter by resource type"
            value={variant === 'panel' ? (draftValues.resourceType ?? null) : (value.resourceType ?? null)}
            options={resourceTypes.map((rt) => ({ label: rt, value: rt }))}
            onChange={(e) => {
              const newValue = e.value as string | undefined
              if (variant === 'panel') {
                setDraftField('resourceType', newValue)
              } else {
                setField('resourceType', newValue)
              }
            }}
            placeholder="Select resource type"
            showClear
            className="w-full"
            loading={resourceTypesLoading}
            pt={{
              root: { className: 'border-gray-200 rounded-sm shadow-sm' },
              input: { className: 'text-sm py-2.5' },
              panel: { className: 'rounded-sm shadow-lg border border-gray-200' },
            }}
          />
        </label>

        <div className="pt-4 border-t border-gray-200">
          <span className="text-sm font-semibold text-gray-700 flex items-center gap-2 mb-4">
            <i className="pi pi-calendar text-gray-400" />
            Created At
          </span>
          <div className="space-y-4">
            <label className="flex flex-col gap-2">
              <span className="text-sm text-gray-600">Start from</span>
              <Calendar
                aria-label="Filter by start date"
                value={variant === 'panel' ? (draftValues.from ?? null) : (value.from ?? null)}
                showIcon
                readOnlyInput
                placeholder="Select date"
                onChange={(e) => {
                  const date = e.value as Date | null
                  if (variant === 'panel') {
                    setDraftField('from', date ?? undefined)
                  } else {
                    setField('from', date ?? undefined)
                  }
                }}
                pt={{
                  root: { className: 'border-gray-200 rounded-sm shadow-sm' },
                  input: { className: 'text-sm py-2.5' },
                  panel: { className: 'rounded-sm shadow-lg border border-gray-200' },
                }}
              />
            </label>

            <label className="flex flex-col gap-2">
              <span className="text-sm text-gray-600">End to</span>
              <Calendar
                aria-label="Filter by end date"
                value={variant === 'panel' ? (draftValues.to ?? null) : (value.to ?? null)}
                showIcon
                readOnlyInput
                placeholder="Select date"
                onChange={(e) => {
                  const date = e.value as Date | null
                  if (variant === 'panel') {
                    setDraftField('to', date ?? undefined)
                  } else {
                    setField('to', date ?? undefined)
                  }
                }}
                pt={{
                  root: { className: 'border-gray-200 rounded-sm shadow-sm' },
                  input: { className: 'text-sm py-2.5' },
                  panel: { className: 'rounded-sm shadow-lg border border-gray-200' },
                }}
              />
            </label>
          </div>
        </div>
      </div>
    </>
  )

  if (variant === 'panel') {
    return (
      <div className="flex flex-col h-full">
        <div className="flex justify-end mb-4">
          <button
            type="button"
            onClick={() => {
              setDraftValues({})
              onChange({})
            }}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-orange-50 border border-orange-300 rounded-sm hover:bg-orange-100 transition-colors"
          >
            Clear filter
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">
          <div className="flex flex-col gap-4">
            {fields}
          </div>
        </div>
        <div className="flex gap-2 pt-4 border-t border-gray-200 mt-4">
          <button
            type="button"
            onClick={() => {
              setDraftValues(value)
              onCancel?.()
            }}
            className="flex-1 h-10 px-4 rounded-sm text-sm font-medium border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => {
              onChange(draftValues)
              onApply?.()
            }}
            className="flex-1 h-10 px-4 rounded-sm text-sm font-medium bg-[#3454cf] text-white hover:bg-[#2a45a8] transition-colors"
          >
            Apply Filters
          </button>
        </div>
      </div>
    )
  }

  // Sidebar variant uses the original setField function
  function setField<K extends keyof AuditFilters>(
    key: K,
    next: AuditFilters[K] | undefined,
  ): void {
    const cleaned = preservedFields(value)
    if (next === undefined || next === '' || next === null) {
      delete cleaned[key]
    } else {
      cleaned[key] = next
    }
    onChange(cleaned)
  }

  return (
    <FilterSidebarShell title="Filters" onClear={() => onChange({})}>
      {fields}
    </FilterSidebarShell>
  )
}

export default AuditLogFilterSidebar
